import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import DataTime from 'react-datetime';
import OutsideClickHandler from 'react-outside-click-handler';
import { db } from '../../firebase/firebaseConfigs';
import { format } from 'date-fns';
import { FirestoreCollection } from '@react-firebase/firestore';
import Media from 'react-media';

import LoadingSection from '../LoadingSection/LoadingSection';
import Locations from '../Locations/Locations';

import { ReactComponent as CalendarIcon } from '../../Components/Icons/calendar.svg';
import { ReactComponent as FileIcon } from '../../Components/Icons/file.svg';
import { ReactComponent as Alert } from '../../Components/Icons/alertTrans.svg';
import { ReactComponent as Right } from '../../Components/Icons/right.svg';

import './ProjectPlanner.scss';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import ReactDOMServer from 'react-dom/server';

function convertToString(data: string | ArrayBuffer | null): string {
	if (data === null) {
		return 'null'; // Handle null case separately
	} else if (typeof data === 'string') {
		return data; // If it's already a string, return as is
	} else if (data instanceof ArrayBuffer) {
		// Convert ArrayBuffer to a string
		const uintArray = new Uint8Array(data);
		const charArray = uintArray.map((byte) => +String.fromCharCode(byte));
		return charArray.join('');
	} else {
		// Handle other data types, if needed
		return String(data);
	}
}

interface ProjectPlannerProps {
	attachFile?: boolean;
	developer?: boolean;
	title?: string;
	titleUa: string;
	subTitle?: string;
	subTitleUa?: string;
	replyEmail?: string;
	vacancyId?: number;
}

interface FormData {
	name: string;
	lastName: string;
	companyName: string;
	title: string;
	email: string;
	phone: string;
	service: string;
	data: string;
	text: string;
	file: string | ArrayBuffer | null;
	terms: boolean;
	subscribe: boolean;
	interests: string;
	location: string;
	salary: string;
}

export default function ProjectPlanner({
	attachFile,
	developer,
	title,
	titleUa,
	subTitle,
	subTitleUa,
	replyEmail,
	vacancyId,
}: ProjectPlannerProps) {
	const { register, handleSubmit, errors } = useForm();
	const [formData, setFormData] = useState<FormData>({
		name: '',
		lastName: '',
		companyName: '',
		title: '',
		email: '',
		phone: '',
		service: '',
		data: '',
		text: '',
		interests: '',
		location: '',
		salary: '',
		file: null,
		terms: false,
		subscribe: true,
	});
	const [fileName, setFileName] = useState('');
	const [emailTemplate, setEmailTemplate] = useState<string>('');
	const [emailTemplateSubscribe, setEmailTemplateSubscribe] = useState<string>('');
	const [emailTemplateSubscribeR, setEmailTemplateSubscribeR] = useState<string>('');
	const [replyEmailLoc, setReplyEmailLoc] = useState('');
	const [sendStatus, setSendStatus] = useState<number | undefined>();
	const [fileLoadStatus, setFileLoadStatus] = useState<number | undefined>();
	const [showCalendar, setShowCalendar] = useState(false);
	const [disableSendButton, setDisableSendButton] = useState(false);
	const [emailFile, setEmailFile] = useState<string | ArrayBuffer | null>(null);
	const [fileFormat, setFileFormat] = useState<string>('');

	const selectedJob = localStorage.getItem('selectedJob');

	const [openedServiceSelector, setOpenedServiceSelector] = useState(false);
	const [language] = useSessionStorage('language', '');

	const getBase64 = (file: any) => {
		setFileFormat(file.name.slice((Math.max(0, file.name.lastIndexOf('.')) || Infinity) + 1));
		const maxFileSize = 10000000;
		if (file.size < maxFileSize) {
			var reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				setFormData({ ...formData, file: reader.result });
				setEmailFile(reader.result);
			};
			reader.onerror = (error) => {
				console.log('Error: ', error);
			};
			setFileLoadStatus(200);
		} else {
			setFileLoadStatus(400);
			setFormData({
				...formData,
				file: null,
			});
			setFileName('');
		}
	};

	const updateInput = (e: any) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
		setSendStatus(undefined);
	};
	const onSubmit = () => {
		sendEmail();
		sendApplicant();
		setSendStatus(undefined);
	};
	const sendEmail = () => {
		setDisableSendButton(true);

		db.collection('completedForms')
			.add({
				name: formData.name,
				lastName: formData.lastName,
				companyName: formData.companyName,
				title: formData.title,
				email: formData.email,
				phone: formData.phone,
				service: formData.service,
				dataOnCall: formData.data,
				text: formData.text,
				interests: formData.interests,
				location: formData.location,
				salary: formData.salary,
				file: formData.file,
				subscribe: formData.subscribe,
				selectedJob: selectedJob,
				developer: developer ? true : false,
				letsTalk: !developer ? true : false,
				createDate: new Date(),
			})
			.then(() => setSendStatus(200))
			.catch(() => setSendStatus(400));
		db.collection('buildEmail').add({
			to: [replyEmailLoc],
			message: {
				subject: developer
					? 'New candidate record has been added by website'
					: 'New contact record has been added by website - Global IT Support',
				html: emailTemplate,
				attachments: emailFile
					? [
							{
								filename: fileName,
								path: emailFile,
							},
					  ]
					: null,
			},
		});

		formData.subscribe &&
			db.collection('buildEmail').add({
				to: formData.email,
				message: {
					subject: 'You have subscribed to Global IT Support',
					html: emailTemplateSubscribe,
				},
			});

		formData.subscribe &&
			db.collection('buildEmail').add({
				to: replyEmailLoc,
				message: {
					subject: 'New subscribe to Global IT Support',
					html: emailTemplateSubscribeR,
				},
			});

		setFormData({
			name: '',
			lastName: '',
			companyName: '',
			title: '',
			email: '',
			phone: '',
			service: '',
			data: '',
			text: '',
			interests: '',
			location: '',
			salary: '',
			file: null,
			terms: false,
			subscribe: true,
		});
		setFileName('');
		setDisableSendButton(false);
	};

	const sendApplicant = () => {
		if (vacancyId) {
			var formdata = new FormData();
			formdata.append('applicant_name', formData.name);
			formdata.append('applicant_last_name', formData.lastName);
			formdata.append('email', formData.email);
			formdata.append('phone_number', formData.phone);
			formdata.append('vacancy_id', vacancyId.toString());
			formdata.append('interests', formData.interests);
			formdata.append('location', formData.location);
			formdata.append('salary', formData.salary);
			formdata.append('details', formData.text);
			formdata.append('cv_file', convertToString(formData.file));
			formdata.append('file_format', fileFormat);

			var requestOptions = {
				method: 'POST',
				body: formdata,
				// redirect: 'follow',
			};

			fetch('https://my.gisua.com/create-applicant?api_key=eea0b382-29c1-4kolia-9077-fb9396c2a344', requestOptions)
				.then((response) => response.text())
				.then((result) => console.log(result))
				.catch((error) => console.log('error', error));
		}
	};

	return (
		<FirestoreCollection path="/projectPlanner/">
			{(data) => {
				if (data.isLoading) {
					return <LoadingSection />;
				}

				setReplyEmailLoc(replyEmail ? replyEmail : data.value[0].replyEmail);

				const emailTemplateSubscribe = ReactDOMServer.renderToString(
					<html>
						<head>
							<meta charSet="utf-8" />
						</head>
						<body>
							<div
								style={{
									width: '600px',
									margin: '40px auto 0',
									padding: '0 20px',
								}}
							>
								<img
									alt=""
									style={{ width: '100%' }}
									src="https://firebasestorage.googleapis.com/v0/b/admin-gisua.appspot.com/o/headRes.png?alt=media&token=a0d7cf32-7856-468f-9784-4adfa65247eb"
								/>
								<div
									style={{
										textAlign: 'center',
										padding: '15px 20px 0',
										fontSize: '16px',
										fontWeight: 'bold',
										maxWidth: '750px',
										margin: '0 auto',
										color: '#24292e',
									}}
								>
									Thank you for subscribing! The latest vacancies will be sent to you regularly.
								</div>
							</div>
						</body>
					</html>
				);

				setEmailTemplateSubscribe(emailTemplateSubscribe);

				const emailTemplateSubscribeR = ReactDOMServer.renderToString(
					<html>
						<head>
							<meta charSet="utf-8" />
						</head>
						<body>
							<div
								style={{
									width: '600px',
									margin: '40px auto 0',
									padding: '0 20px',
								}}
							>
								<img
									alt=""
									style={{ width: '100%' }}
									src="https://firebasestorage.googleapis.com/v0/b/admin-gisua.appspot.com/o/headRes.png?alt=media&token=a0d7cf32-7856-468f-9784-4adfa65247eb"
								/>
								<div
									style={{
										textAlign: 'center',
										padding: '15px 20px 0',
										fontSize: '16px',
										fontWeight: 'bold',
										maxWidth: '750px',
										margin: '0 auto',
										color: '#24292e',
									}}
								>
									{`${formData.email} subscribed to latest vacancies.`}
								</div>
							</div>
						</body>
					</html>
				);

				setEmailTemplateSubscribeR(emailTemplateSubscribeR);

				const template = ReactDOMServer.renderToString(
					<html>
						<head>
							<meta charSet="utf-8" />
						</head>
						<body>
							<div
								style={{
									width: '600px',
									margin: '40px auto 0',
									padding: '0 20px',
								}}
							>
								<img
									alt=""
									style={{ width: '100%' }}
									src="https://firebasestorage.googleapis.com/v0/b/admin-gisua.appspot.com/o/headRes.png?alt=media&token=a0d7cf32-7856-468f-9784-4adfa65247eb"
								/>
								<div
									style={{
										textAlign: 'center',
										padding: '15px 20px 0',
										fontSize: '16px',
										fontWeight: 'bold',
										maxWidth: '750px',
										margin: '0 auto',
										textTransform: 'uppercase',
										color: '#24292e',
									}}
								>
									{developer ? data.value[0].defaultTitle : data.value[0].formHeadTitle}
								</div>
								<div style={{ width: '100%' }}>
									{developer && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Position:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{selectedJob}
											</div>
										</div>
									)}
									<div
										style={{
											maxWidth: '1150px',
											margin: '0 auto',
											padding: '20px 0',
										}}
									>
										<div
											style={{
												color: '#24292e',
												fontWeight: 'bold',
												padding: '17px 0',
												minWidth: '200px',
												fontSize: '14px',
												whiteSpace: 'nowrap',
												display: 'inline-block',
											}}
										>
											Name:
										</div>
										<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
											{formData.name}
										</div>
									</div>
									{formData.lastName && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Last Name:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.lastName}
											</div>
										</div>
									)}
									{formData.email && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Email:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.email}
											</div>
										</div>
									)}
									{formData.phone && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Phone:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.phone}
											</div>
										</div>
									)}
									{formData.companyName && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Company Name:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.companyName}
											</div>
										</div>
									)}
									{formData.title && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Title:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.title}
											</div>
										</div>
									)}
									{formData.service && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Service:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.service}
											</div>
										</div>
									)}
									{formData.interests && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Professional interests:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.interests}
											</div>
										</div>
									)}
									{formData.location && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Current location:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.location}
											</div>
										</div>
									)}
									{formData.salary && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Salary expectations:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.salary}
											</div>
										</div>
									)}
									{formData.data && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Schedule a call:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.data}
											</div>
										</div>
									)}
									{formData.text && (
										<div
											style={{
												maxWidth: '1150px',
												margin: '0 auto',
												padding: '20px 0',
											}}
										>
											<div
												style={{
													color: '#24292e',
													fontWeight: 'bold',
													padding: '17px 0',
													minWidth: '200px',
													fontSize: '14px',
													whiteSpace: 'nowrap',
													display: 'inline-block',
												}}
											>
												Message:
											</div>
											<div style={{ fontSize: '14px', fontWeight: 'bold', display: 'inline-block', color: '#24292e' }}>
												{formData.text}
											</div>
										</div>
									)}
								</div>
							</div>
						</body>
					</html>
				);

				return (
					<div data-testclass="projectPlannerSectionScroll" id="projectPlannerSectionScroll">
						<div className={developer ? 'projectPlannerWhiteBackground' : 'projectPlannerBackground'}>
							{developer && (
								<Media query="(max-width: 1177px)">
									{(matches) =>
										matches && (
											<>
												<div className="projectPlannerMainTitle">
													{language === 'ua' ? titleUa ?? data.value[0].defaultTitleUa : title ?? data.value[0].defaultTitle}
												</div>
												<div className="projectPlannerSubTitle">
													{language === 'ua'
														? subTitleUa ?? data.value[0].defaultSubTitleUa
														: subTitle ?? data.value[0].defaultSubTitle}
												</div>
											</>
										)
									}
								</Media>
							)}
							<div className={developer ? 'projectPlannerFullWidth' : 'projectPlanner'}>
								{!developer && (
									<div className="projectPlannerTitleWithLocation">
										<div className="projectPlannerTitleWrapper">
											<div className="projectPlannerMainTitle">
												{language === 'ua' ? titleUa ?? data.value[0].defaultTitleUa : title ?? data.value[0].defaultTitle}
											</div>
											<div className="projectPlannerSubTitle">
												{language === 'ua'
													? subTitleUa ?? data.value[0].defaultSubTitleUa
													: subTitle ?? data.value[0].defaultSubTitle}
											</div>
										</div>
										<Locations />
									</div>
								)}
								<form
									onSubmit={handleSubmit(onSubmit)}
									className={developer ? 'projectPlannerDeveloperForm' : 'projectPlannerForm'}
								>
									{!developer && (
										<div className="projectPlannerTitle">
											{language === 'ua' ? data.value[0].formHeadTitleUa : data.value[0].formHeadTitle}
										</div>
									)}
									<div className="plannerFormTitle">
										{language === 'ua' ? data.value[0].formTitleUa : data.value[0].formTitle}
									</div>
									<div className="plannerFormInputs">
										<div className="inputWrapper">
											<input
												type="text"
												name="name"
												className={errors.name ? 'plannerInputError' : 'plannerInput'}
												placeholder={language === 'ua' ? "Ім'я" : 'Name'}
												ref={register({
													required: 'Required Name',
													minLength: 2,
												})}
												onChange={updateInput}
												value={formData.name || ''}
											/>
											{errors.name && <Alert className="inputErrorAlert" />}
										</div>
										<input
											type="text"
											name="lastName"
											className="plannerInput"
											placeholder={language === 'ua' ? 'Прізвище' : 'Last name'}
											onChange={updateInput}
											value={formData.lastName || ''}
										/>
										{developer ? (
											<input
												type="text"
												name="interests"
												className="plannerInput"
												placeholder={language === 'ua' ? 'Професійні інтереси' : 'Professional interests'}
												onChange={updateInput}
												value={formData.interests || ''}
											/>
										) : (
											<input
												type="text"
												name="companyName"
												className="plannerInput"
												placeholder={language === 'ua' ? 'Назва компанії' : 'Company name'}
												onChange={updateInput}
												value={formData.companyName || ''}
											/>
										)}
										{developer ? (
											<input
												type="text"
												name="location"
												className="plannerInput"
												placeholder={language === 'ua' ? 'Поточне місцезнаходження' : 'Current location'}
												onChange={updateInput}
												value={formData.location || ''}
											/>
										) : (
											<input
												type="text"
												name="title"
												className="plannerInput"
												placeholder={language === 'ua' ? 'Назва' : 'Title'}
												onChange={updateInput}
												value={formData.title || ''}
											/>
										)}
										<div className="inputWrapper">
											<input
												type="text"
												name="email"
												className={errors.email ? 'plannerInputError' : 'plannerInput'}
												placeholder={language === 'ua' ? 'Електронна пошта' : 'Email address'}
												ref={register({
													required: 'Required Email',
													pattern: {
														value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
														message: 'invalid email address',
													},
												})}
												onChange={updateInput}
												value={formData.email || ''}
											/>
											{errors.email && <Alert className="inputErrorAlert" />}
										</div>
										<div className="inputWrapper">
											<input
												type="number"
												name="phone"
												className={errors.phone ? 'plannerInputError' : 'plannerInput'}
												placeholder={language === 'ua' ? 'Телефонний номер' : 'Phone number'}
												ref={register({
													required: 'Required Phone',
													pattern: {
														value: /^\+?[0-9- ]{9,20}$/i,
														message: 'invalid phone number',
													},
												})}
												onChange={updateInput}
												value={formData.phone || ''}
											/>
											{errors.phone && <Alert className="inputErrorAlert" />}
										</div>
										{developer || attachFile ? (
											<div className="plannerInputFileWrapper">
												<FileIcon />
												<input
													type="file"
													name="file"
													id="file"
													className="projectPlannerInputFile"
													onChange={(e) => {
														setFileName(e.target.value);
														getBase64(e.target.files && e.target.files[0]);
													}}
													value={fileName}
												/>
												<label htmlFor="file" className="plannerInputFile">
													{fileName !== ''
														? fileName.replace(/.*[/\\]/, '')
														: language === 'ua'
														? 'Прикріпити резюме'
														: 'Attach CV'}
												</label>
											</div>
										) : (
											<div className={openedServiceSelector ? 'openedServiceSelectorWrapper' : 'serviceSelectorWrapper'}>
												<div
													className={formData.service === '' ? 'serviceSelectorInit' : 'serviceSelector'}
													onClick={() => setOpenedServiceSelector(!openedServiceSelector)}
												>
													{formData.service === ''
														? language === 'ua'
															? 'Яка послуга вас цікавить?'
															: 'Service you are interested in?'
														: formData.service}
													<Right className={openedServiceSelector ? 'openService' : 'closedService'} />
												</div>
												{openedServiceSelector && (
													<div className="serviceSelectorList">
														{data.value[0].services.map((ser: { service: string; serviceUa: string }, index: number) => (
															<div
																key={index}
																className="service"
																onClick={() => {
																	setFormData({ ...formData, service: ser.service });
																	setOpenedServiceSelector(false);
																}}
															>
																{language === 'ua' ? ser.serviceUa : ser.service}
															</div>
														))}
													</div>
												)}
											</div>
										)}
										{developer ? (
											<input
												type="text"
												name="salary"
												className="plannerInput"
												placeholder={language === 'ua' ? 'Очікування щодо зарплати' : 'Salary expectations'}
												onChange={updateInput}
												value={formData.salary || ''}
											/>
										) : (
											<div className="plannerInputCalendarWrapper">
												<CalendarIcon />
												<input
													type="text"
													name="data"
													disabled
													className={showCalendar ? 'plannerInputOpened' : 'plannerInput'}
													placeholder={language === 'ua' ? 'Заплануйте дзвінок' : 'Schedule a call'}
													onChange={updateInput}
													value={formData.data || ''}
												/>
												<div onClick={() => setShowCalendar(true)} className="calendarButton" />
												{showCalendar && (
													<OutsideClickHandler onOutsideClick={() => setShowCalendar(false)}>
														<DataTime
															className={showCalendar ? 'projectPlannerCalendarOpened' : 'projectPlannerCalendar'}
															input={false}
															onChange={(e: any) => setFormData({ ...formData, data: format(e._d, 'dd-MM-yyyy hh:mm aaa') })}
														/>
													</OutsideClickHandler>
												)}
											</div>
										)}
									</div>
									<div className="plannerFormTitle">
										{language === 'ua' ? data.value[0].textInputTitleUa : data.value[0].textInputTitle}
									</div>
									<textarea
										name="text"
										className="plannerTextArea"
										rows={4}
										placeholder={language === 'ua' ? 'Введіть свій текст' : 'Type you text'}
										onChange={updateInput}
										value={formData.text || ''}
									/>

									<div className="projectPlannerCheckboxWrapper">
										<input
											type="checkbox"
											name="terms"
											className="projectPlannerCheckbox"
											ref={register({ required: true })}
											onChange={(e) => setFormData({ ...formData, terms: e.target.checked })}
											checked={formData.terms}
										/>
										<span className={errors.terms ? 'projectPlannerIndicatorError' : 'projectPlannerIndicator'}>
											<span className="projectPlannerCheckIcon" />
										</span>
										<div
											className="projectPlannerCheckboxText"
											dangerouslySetInnerHTML={{
												__html: `${language === 'ua' ? data.value[0].privacyTextUa : data.value[0].privacyText}`,
											}}
										></div>
									</div>

									<div className="projectPlannerCheckboxWrapper">
										<input
											type="checkbox"
											name="subscribe"
											className="projectPlannerCheckbox"
											onChange={(e) => setFormData({ ...formData, subscribe: e.target.checked })}
											checked={formData.subscribe}
										/>
										<span className="projectPlannerIndicator">
											<span className="projectPlannerCheckIcon" />
										</span>
										<div
											className="projectPlannerCheckboxText"
											dangerouslySetInnerHTML={{
												__html: `${language === 'ua' ? data.value[0].subscribeTextUa : data.value[0].subscribeText}`,
											}}
										></div>
									</div>

									<div className="emailStatus">
										{sendStatus === 200 && (
											<div className="plannerSuccess">{language === 'ua' ? 'Успішно' : 'Successfully'}</div>
										)}
										{fileLoadStatus === 400 && (
											<div className="plannerError">
												{language === 'ua' ? 'Розмір файлу має бути менше 10 Мб' : 'File size must be less than 10mb'}
											</div>
										)}
										{sendStatus === 400 && (
											<div className="plannerError">{language === 'ua' ? 'Щось пішло не так' : 'Something went wrong'}</div>
										)}
										{errors.name && errors.email && errors.phone && (
											<div className="plannerError">
												{language === 'ua' ? 'Жодне поле не повинно залишатися порожнім' : 'No field should remain empty'}{' '}
											</div>
										)}
										{errors.terms && (
											<div className="plannerError">
												{language === 'ua'
													? 'Умови та Політика конфіденційності є обов’язковими'
													: 'Terms & Conditions and Privacy Policy is required'}
											</div>
										)}
									</div>
									<button
										type="submit"
										disabled={disableSendButton}
										onClick={() => setEmailTemplate(template)}
										className="plannerButton"
									>
										{language === 'ua' ? data.value[0].buttonTextUa : data.value[0].buttonText}
									</button>
								</form>
								{developer && (
									<div className="projectPlannerTitleWithImage">
										<Media query="(min-width: 1178px)">
											{(matches) =>
												matches && (
													<>
														<div className="projectPlannerTitle">
															{language === 'ua'
																? titleUa ?? data.value[0].defaultTitleUa
																: title ?? data.value[0].defaultTitle}
														</div>
														<div className="projectPlannerSubTitle">
															{language === 'ua'
																? subTitleUa ?? data.value[0].defaultSubTitleUa
																: subTitle ?? data.value[0].defaultSubTitle}
														</div>
													</>
												)
											}
										</Media>
										<div
											className="projectPlannerImage"
											style={{
												backgroundImage:
													data.value[0].defaultPlannerImage && `url(${data.value[0].defaultPlannerImage.original.src})`,
											}}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				);
			}}
		</FirestoreCollection>
	);
}
